import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { goToPostSubmit, readPostSubmitGuestName } from '../lib/postSubmitGuest';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Send, UserPlus, Share2, Gift, Plus, Trash2 } from 'lucide-react';
import { NAV_CATEGORIES, CATEGORIES_ORDER } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import FormLegalNotice from '../components/FormLegalNotice';
import TrustStrip from '../components/TrustStrip';
import {
  getJourneyId,
  trackEvent,
  trackPageLifecycle,
} from '../lib/customerEventTracker';
import { appendLeadToGoogleSheets, isGoogleSheetsConfigured } from '../lib/googleSheetsLead';

/**
 * From UI rows (digit strings), validates: at least one full 10-digit number;
 * rejects any partially filled row; returns unique mobiles in row order.
 */
function collectValidatedReferralPhones(rows) {
  const digitsRows = rows.map((r) => r.replace(/\D/g, '').slice(0, 10));
  const nonEmpty = digitsRows.filter((d) => d.length > 0);
  if (nonEmpty.length === 0) {
    return { ok: false, code: 'empty', phones: [] };
  }
  for (const d of nonEmpty) {
    if (d.length !== 10) {
      return { ok: false, code: 'invalid_partial', phones: [] };
    }
  }
  const phones = [];
  const seen = new Set();
  for (const d of nonEmpty) {
    if (seen.has(d)) continue;
    seen.add(d);
    phones.push(d);
  }
  return { ok: true, code: null, phones };
}

const REFERRAL_SERVICE_ORDER = [...CATEGORIES_ORDER, 'OTHER'];

const orderReferralServiceSelection = (picked) =>
  REFERRAL_SERVICE_ORDER.filter((c) => picked.includes(c));

const Referral = ({ postSubmitMode = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const postSubmitGuestName = useMemo(() => readPostSubmitGuestName(), []);
  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'REFERRAL_FORM',
      form_name: 'ReferralForm',
      open_event_name: 'referral_form_open',
    }),
    [],
  );
  const steps = useMemo(() => [
    { icon: UserPlus, title: t('ref_form.step_join_title', 'Join the program'), desc: t('ref_form.step_join_desc', 'Register yourself as a referrer with SolveTax. It takes less than a minute.') },
    { icon: Share2, title: t('ref_form.step_share_title', 'Share with us'), desc: t('ref_form.step_share_desc', 'Share your friends, family and business associates Referral Phone Numbers with us.') },
    { icon: Gift, title: t('ref_form.step_earn_title', 'Earn rewards'), desc: t('ref_form.step_earn_desc', 'Receive ₹200 for every successful referral that files a return with us.') },
  ], [t]);

  const [data, setData] = useState({
    your_name: '',
    phone_number: '',
    referral_phone_rows: [''],
    service_required: [],
    your_message: '',
  });
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setData({
      your_name: '',
      phone_number: '',
      referral_phone_rows: [''],
      service_required: [],
      your_message: '',
    });
  };

  const setReferralRow = (index, value) => {
    const next = [...data.referral_phone_rows];
    next[index] = value.replace(/\D/g, '').slice(0, 10);
    setData({ ...data, referral_phone_rows: next });
  };

  const addReferralRow = () => {
    setData({ ...data, referral_phone_rows: [...data.referral_phone_rows, ''] });
  };

  const removeReferralRow = (index) => {
    if (data.referral_phone_rows.length <= 1) return;
    setData({
      ...data,
      referral_phone_rows: data.referral_phone_rows.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!data.your_name.trim()) {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Name is required',
      });
      toast.error(t('ref_form.err_name', 'Your name is required.'));
      return;
    }
    if (!/^[0-9]{10}$/.test(data.phone_number)) {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Phone number must be valid',
      });
      toast.error(t('ref_form.err_phone', 'Phone number must be a valid 10-digit mobile number.'));
      return;
    }
    const referralCollected = collectValidatedReferralPhones(data.referral_phone_rows);
    if (!referralCollected.ok && referralCollected.code === 'empty') {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Referral phones missing',
      });
      toast.error(t('ref_form.err_ref_phones_empty', 'Enter at least one valid 10-digit referral mobile number.'));
      return;
    }
    if (!referralCollected.ok && referralCollected.code === 'invalid_partial') {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Referral phones incomplete rows',
      });
      toast.error(t('ref_form.err_ref_phones_invalid', 'Each referral number must be exactly 10 digits. Check what you entered.'));
      return;
    }
    const referralPhones = referralCollected.phones;
    if (referralPhones.includes(data.phone_number)) {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Referral list includes referrer phone',
      });
      toast.error(t('ref_form.err_ref_phone_same', 'Referral list cannot include your own mobile number.'));
      return;
    }
    if (!Array.isArray(data.service_required) || data.service_required.length === 0) {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Service required is missing',
      });
      toast.error(t('ref_form.err_service', 'Service required for referral is mandatory.'));
      return;
    }
    const servicesOrdered = orderReferralServiceSelection(data.service_required);
    const serviceLabelsOrdered = servicesOrdered.map((code) => {
      if (code === 'OTHER') return t('contact.other_services', 'Other Services');
      const cat = NAV_CATEGORIES.find((c) => c.category === code);
      return cat ? t(`category.${cat.category}`, cat.label) : code;
    });
    const payload = {
      your_name: data.your_name.trim(),
      phone_number: data.phone_number.trim(),
      email_address: null,
      service_required: serviceLabelsOrdered.join(', '),
      services_required: servicesOrdered,
      referal_phone_number: referralPhones.join(','),
      your_message: data.your_message.trim() || null,
    };

    setLoading(true);
    trackEvent({
      event_name: 'referral_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'ReferralForm',
      funnel_name: 'REFERRAL_FORM',
      funnel_step_number: 2,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('REFERRAL_FORM'),
      phone_number: payload.phone_number,
      service_code: payload.services_required.length ? payload.services_required.join(',') : null,
      event_label: `referrals_${referralPhones.length}`,
    });
    try {
      if (!isGoogleSheetsConfigured()) {
        trackEvent({
          event_name: 'referral_submit_failed',
          event_action: 'submit',
          event_status: 'failed',
          event_category: 'form',
          form_name: 'ReferralForm',
          funnel_name: 'REFERRAL_FORM',
          funnel_step_number: 2,
          funnel_step_name: 'submit',
          journey_id: getJourneyId('REFERRAL_FORM'),
          phone_number: payload.phone_number,
          service_code: payload.services_required.length ? payload.services_required.join(',') : null,
          error_code: 'missing_sheets_env',
          error_message: 'REACT_APP_GOOGLE_SHEETS_WEBAPP_URL not set',
        });
        toast.error(
          t(
            'ref_form.err_sheets_missing',
            'Google Sheets URL is missing. Add REACT_APP_GOOGLE_SHEETS_WEBAPP_URL to frontend/.env and restart.',
          ),
        );
        return;
      }
      appendLeadToGoogleSheets({
        form: 'referral',
        summary: `${payload.your_name} | ${payload.phone_number} | refs: ${payload.referal_phone_number} | ${payload.service_required}`,
        detail: payload,
      });
      trackEvent({
        event_name: 'referral_submit_success',
        event_action: 'submit',
        event_status: 'success',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM', { reset: true }),
        phone_number: payload.phone_number,
        service_code: payload.services_required.length ? payload.services_required.join(',') : null,
        api_name: 'google_sheets',
        api_status_code: null,
      });
      toast.success(t('ref_form.toast_ok', 'Referral request submitted. Our support team will contact you within 2 hours.'));
      goToPostSubmit(navigate, payload.your_name);
    } catch (err) {
      const msg = err?.message || 'Request failed';
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        phone_number: payload.phone_number,
        service_code: payload.services_required.length ? payload.services_required.join(',') : null,
        error_code: 'unexpected',
        error_message: msg,
      });
      toast.error(t('contact.toast_backend', 'Backend: {msg}', { msg }));
    } finally {
      setLoading(false);
    }
  };

  const formTitle = postSubmitMode
    ? t('post_submit.ref_form_title', 'Submit Referral')
    : t('ref_form.form_title', 'Submit Referral');
  const formSubtitle = postSubmitMode
    ? t(
      'post_submit.ref_form_sub',
      'Refer friends and earn rewards. Fill the form below and our team will contact you within 2 hours.',
    )
    : t('ref_form.form_sub', 'Fill out the form and our support team will contact you within 2 hours.');

  return (
  <div className={postSubmitMode ? 'pt-20' : 'pt-28'}>
    {postSubmitMode ? (
      <section className="py-8 hero-gradient">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Card className="p-6 md:p-8 rounded-2xl border border-gray-100 shadow-lg bg-white text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-md mb-4">
              <Gift className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#0f2540] mb-2">
              {postSubmitGuestName
                ? t('post_submit.ref_title_named', 'Thank you {name}! Your request is submitted.', {
                  name: postSubmitGuestName,
                })
                : t('post_submit.ref_title', 'Thank you! Your request is submitted.')}
            </h1>
            <p className="text-base md:text-lg text-gray-600">
              {t(
                'post_submit.ref_message',
                'Earn ₹200 for every successful referral. Submit referral details below and our team will follow up quickly.',
              )}
            </p>
          </Card>
        </div>
      </section>
    ) : (
      <section className="py-14 hero-gradient">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
            <h1 className="ui-title-xl mb-3">
              {t('ref_form.hero_title', 'Our')} <span className="text-[#14A098]">{t('ref_form.hero_accent', 'Referral Program')}</span>
            </h1>
            <p className="ui-body mb-6">{t('ref_form.hero_sub', 'Fill out the form and our support team will contact you within 2 hours.')}</p>
            <TrustStrip />
        </div>
      </section>
    )}

      <section className="py-14 bg-teal-50/20">
        <div className="w-full max-w-[1680px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-4 space-y-4 order-2 lg:order-1">
            <div className="rounded-2xl bg-white border border-gray-100 shadow-lg p-5 md:p-7 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-md mb-5">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <div className="text-5xl md:text-7xl font-extrabold text-[#0f2540] leading-none">₹200</div>
              <div className="text-sm md:text-lg uppercase tracking-wider text-[#14A098] font-semibold mt-2">{t('referral.reward_label', 'Reward per referral')}</div>
              <p className="text-gray-600 mt-3 text-sm md:text-base">{t('ref_form.reward_line', 'Get ₹200 for every successful referral.')}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white shadow-lg p-4 md:p-5">
              <h3 className="text-lg font-bold text-[#0f2540] mb-4">{t('ref_form.how_title', 'How Referral Works')}</h3>
              <div className="space-y-3">
                {steps.map((s, i) => (
                  <div key={s.title} className="rounded-xl border border-teal-100 bg-teal-50/40 p-3 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-sm shrink-0">
                      <s.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#14A098] mb-0.5">{t('common.step_label', 'Step {n}', { n: i + 1 })}</div>
                      <h4 className="text-base font-bold text-[#0f2540] leading-tight">{s.title}</h4>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">{s.desc}</p>
                    </div>
          </div>
        ))}
      </div>
            </div>
          </div>
          <div className="lg:col-span-8 order-1 lg:order-2">
            <Card id="referral-form" className="p-6 md:p-8 rounded-2xl border border-gray-100 shadow-lg bg-white">
              <h2 className="text-2xl font-bold text-[#0f2540] mb-1">{formTitle}</h2>
              <p className="text-sm text-gray-500 mb-6">{formSubtitle}</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ref-your_name" className="text-sm mb-2 block">{t('ref_form.label_name', 'Your Name')} <span className="text-rose-500">*</span></Label>
                    <Input
                      id="ref-your_name"
                      name="your_name"
                      autoComplete="name"
                      value={data.your_name}
                      onChange={(e) => setData({ ...data, your_name: e.target.value })}
                      placeholder={t('contact.ph_name', 'Full name')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ref-phone_number" className="text-sm mb-2 block">{t('ref_form.label_phone', 'Your mobile number')} <span className="text-rose-500">*</span></Label>
                    <Input
                      id="ref-phone_number"
                      name="phone_number"
                      autoComplete="tel"
                      value={data.phone_number}
                      onChange={(e) => setData({ ...data, phone_number: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      placeholder={t('contact.ph_phone', '10-digit mobile')}
                    />
                  </div>
                  <div className="md:col-span-2 space-y-3">
                    <Label htmlFor="ref-referral-phone-0" className="text-sm mb-0 block">{t('ref_form.label_ref_phone', 'Referral mobile numbers')} <span className="text-rose-500">*</span></Label>
                    <div className="space-y-2">
                      {data.referral_phone_rows.map((val, idx) => (
                        <div key={`ref-phone-row-${idx}`} className="flex gap-2 items-center">
                          <Input
                            id={idx === 0 ? 'ref-referral-phone-0' : undefined}
                            name={`referral_phone_${idx}`}
                            autoComplete="tel"
                            inputMode="numeric"
                            value={val}
                            onChange={(e) => setReferralRow(idx, e.target.value)}
                            placeholder={t('ref_form.ph_ref_phone_row', '10-digit mobile')}
                            className="flex-1 min-w-0"
                          />
                          {data.referral_phone_rows.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="shrink-0 h-10 w-10 rounded-full border-gray-300 text-rose-600 hover:bg-rose-50"
                              onClick={() => removeReferralRow(idx)}
                              aria-label={t('ref_form.remove_referral_phone', 'Remove number')}
                            >
                              <Trash2 className="w-4 h-4" aria-hidden />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full border-[#14A098] text-[#14A098] hover:bg-[#14A098] hover:text-white"
                      onClick={addReferralRow}
                    >
                      <Plus className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                      {t('ref_form.add_referral_phone', 'Add another number')}
                    </Button>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="ref-service_required" className="text-sm mb-2 block">{t('ref_form.label_service', 'Service required for referral')} <span className="text-rose-500">*</span></Label>
                  <p className="text-xs text-gray-500 mb-2">
                    {t('ref_form.service_multi_hint', 'Select all categories that match this referral.')}
                  </p>
                  <div
                    id="ref-service_required"
                    role="group"
                    aria-label={t('ref_form.label_service', 'Service required for referral')}
                    className="rounded-md border border-input bg-background p-3 space-y-2.5 max-h-52 overflow-y-auto"
                  >
                    {NAV_CATEGORIES.map((cat) => {
                      const checked = data.service_required.includes(cat.category);
                      return (
                        <label
                          key={cat.category}
                          htmlFor={`ref-service-${cat.category}`}
                          className="flex items-start gap-2.5 cursor-pointer text-sm font-medium text-[#0f2540] leading-snug"
                        >
                          <Checkbox
                            id={`ref-service-${cat.category}`}
                            className="mt-0.5"
                            checked={checked}
                            onCheckedChange={(c) => {
                              const on = c === true;
                              setData((prev) => ({
                                ...prev,
                                service_required: on
                                  ? orderReferralServiceSelection([...new Set([...prev.service_required, cat.category])])
                                  : prev.service_required.filter((s) => s !== cat.category),
                              }));
                            }}
                          />
                          <span>{t(`category.${cat.category}`, cat.label)}</span>
                        </label>
                      );
                    })}
                    <label
                      htmlFor="ref-service-OTHER"
                      className="flex items-start gap-2.5 cursor-pointer text-sm font-medium text-[#0f2540] leading-snug"
                    >
                      <Checkbox
                        id="ref-service-OTHER"
                        className="mt-0.5"
                        checked={data.service_required.includes('OTHER')}
                        onCheckedChange={(c) => {
                          const on = c === true;
                          setData((prev) => ({
                            ...prev,
                            service_required: on
                              ? orderReferralServiceSelection([...new Set([...prev.service_required, 'OTHER'])])
                              : prev.service_required.filter((s) => s !== 'OTHER'),
                          }));
                        }}
                      />
                      <span>{t('contact.other_services', 'Other Services')}</span>
                    </label>
                  </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="ref-your_message" className="text-sm mb-2 block">{t('ref_form.label_message', 'Your Message')}</Label>
                  <Textarea
                    id="ref-your_message"
                    name="your_message"
                    rows={5}
                    value={data.your_message}
                    onChange={(e) => setData({ ...data, your_message: e.target.value })}
                    placeholder={t('contact.ph_message', 'Tell us about your requirement')}
                  />
                </div>

                <div className="flex flex-wrap gap-3 pt-2 items-center">
                  <Button type="submit" disabled={loading} size="lg" className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-8 h-12 shadow-md min-w-[200px]">
                    {loading ? t('contact.btn_sending', 'Sending...') : (<>{t('form.submit_details', 'Submit Details')} <Send className="w-4 h-4 ml-2" /></>)}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm} className="rounded-full h-12 border-gray-300">
                    {t('contact.btn_reset', 'Reset')}
                  </Button>
                  <FormLegalNotice />
                </div>
              </form>
            </Card>
        </div>
      </div>
    </section>
  </div>
);
};

export default Referral;
